import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { FormRow } from '../ui/FormRow';
import { Spinner } from '../ui/Spinner';
import { fmt } from '../../lib/formatters';
import { toast } from '../../lib/toast';

type LineaEntrega = {
  id: string;
  sku_cliente: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  descripcion: string | null;
  unidades: number;
  unidades_entregadas: number | null;
  cancelada: boolean;
};

/**
 * «Entregar pedido» desde el listado (CEO, 26/10/2026): pide la fecha y, por
 * cada línea pendiente, las unidades que se entregan. Por defecto todo lo que
 * falta (entrega completa); se puede bajar línea a línea para una entrega
 * parcial (0 = esa línea no se entrega ahora). Se registra todo en una sola
 * operación (POST /pedidos-cliente/:id/entregar): o todas las líneas o ninguna.
 */
export function EntregarPedidoModal({ pedidoId, titulo, onClose }: { pedidoId: string; titulo: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['pedido-cliente', pedidoId, 'full'],
    queryFn: () => api.get<{ lineas: LineaEntrega[] }>(`/pedidos-cliente/${pedidoId}/full`),
  });
  const pendientes = (q.data?.lineas ?? [])
    .filter((l) => !l.cancelada)
    .map((l) => ({ ...l, faltan: Math.max(0, Number(l.unidades) - Number(l.unidades_entregadas ?? 0)) }))
    .filter((l) => l.faltan > 0);

  const [fecha, setFecha] = useState(new Date().toLocaleDateString('sv-SE'));
  const [notas, setNotas] = useState('');
  const [uds, setUds] = useState<Record<string, string>>({});
  // Al cargar, todo lo que falta (entrega completa).
  useEffect(() => {
    if (!q.data) return;
    setUds(Object.fromEntries(pendientes.map((l) => [l.id, String(l.faltan)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const valor = (id: string) => Number(uds[id] ?? 0);
  const errores = pendientes.filter((l) => {
    const v = valor(l.id);
    return !Number.isInteger(v) || v < 0 || v > l.faltan;
  });
  const aEntregar = pendientes.filter((l) => valor(l.id) > 0);
  const total = aEntregar.reduce((a, l) => a + valor(l.id), 0);
  const completa = pendientes.length > 0 && pendientes.every((l) => valor(l.id) === l.faltan);

  const entregar = useMutation({
    mutationFn: () => api.post<{ lineas: number; unidades: number; estado_pedido: { estado_nuevo: string } | null }>(
      `/pedidos-cliente/${pedidoId}/entregar`,
      { fecha_entrega: fecha, notas: notas.trim() || undefined, lineas: aEntregar.map((l) => ({ linea_id: l.id, unidades: valor(l.id) })) },
    ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoId] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      const estado = r.estado_pedido?.estado_nuevo;
      toast.success('Entrega registrada', `${fmt.int(r.unidades)} uds en ${fmt.int(r.lineas)} línea(s) el ${fmt.date(fecha)}.${estado === 'FINALIZADO' ? ' Pedido entregado del todo: pasa a FINALIZADO.' : estado === 'ENTREGA_PARCIAL' ? ' El pedido queda en entrega parcial.' : ''}`);
      onClose();
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const m = msg.match(/→\s*\d+:\s*(.+)$/);
      toast.error('No se registró la entrega', m ? m[1] : msg);
    },
  });

  const poner = (todo: boolean) => setUds(Object.fromEntries(pendientes.map((l) => [l.id, todo ? String(l.faltan) : '0'])));

  return (
    <Modal
      open
      onClose={onClose}
      title={`Entregar pedido ${titulo}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={entregar.isPending}>Cancelar</Button>
          <Button variant="save" onClick={() => entregar.mutate()} disabled={!fecha || total === 0 || errores.length > 0 || entregar.isPending}>
            {entregar.isPending ? 'Guardando…' : completa ? `📦 Entregar todo (${fmt.int(total)} uds)` : `📦 Entrega parcial (${fmt.int(total)} uds)`}
          </Button>
        </>
      }
    >
      {q.isLoading ? <Spinner /> : pendientes.length === 0 ? (
        <p className="text-sm text-slate-600">Este pedido no tiene unidades pendientes de entregar.</p>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormRow label="Fecha de entrega" required>
              <Input type="date" value={fecha} max={new Date().toLocaleDateString('sv-SE')} onChange={(e) => setFecha(e.target.value)} />
            </FormRow>
            <FormRow label="Notas" hint="Nº de albarán, transportista, incidencias…">
              <Input value={notas} onChange={(e) => setNotas(e.target.value)} />
            </FormRow>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-slate-600">Unidades por línea (0 = no se entrega ahora):</span>
            <button type="button" onClick={() => poner(true)} className="cursor-pointer font-semibold text-accent-700 hover:underline">Todo lo pendiente</button>
            <button type="button" onClick={() => poner(false)} className="cursor-pointer text-slate-500 hover:underline">Poner todo a 0</button>
          </div>
          <div className="max-h-[50vh] overflow-auto rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-2 py-1.5 text-left">Producto</th>
                  <th className="px-2 py-1.5 text-right">Pedidas</th>
                  <th className="px-2 py-1.5 text-right">Entregadas</th>
                  <th className="px-2 py-1.5 text-right">Faltan</th>
                  <th className="px-2 py-1.5 text-right">Entregar ahora</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendientes.map((l) => {
                  const mal = errores.includes(l);
                  return (
                    <tr key={l.id} className={valor(l.id) === l.faltan ? 'bg-emerald-50' : ''}>
                      <td className="px-2 py-1.5 text-xs">
                        <span className="font-mono">{l.sku_cliente ?? l.producto_sku ?? '—'}</span>
                        <span className="text-slate-500"> · {l.producto_nombre ?? l.descripcion ?? ''}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(l.unidades)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(l.unidades_entregadas ?? 0)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(l.faltan)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Input
                          type="number" min={0} max={l.faltan} step={1}
                          aria-label={`Unidades a entregar de ${l.sku_cliente ?? l.producto_sku ?? 'la línea'}`}
                          value={uds[l.id] ?? ''}
                          onChange={(e) => setUds((u) => ({ ...u, [l.id]: e.target.value }))}
                          className={`ml-auto w-24 text-right ${mal ? 'border-rose-400 ring-1 ring-rose-200' : ''}`}
                          title={mal ? `Entre 0 y ${l.faltan}` : undefined}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {errores.length > 0 && <p className="text-xs text-rose-600">Revisa las líneas en rojo: las unidades van de 0 a lo que falta.</p>}
        </div>
      )}
    </Modal>
  );
}
